import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { NotificationBell } from './NotificationBell';
import { FlagIcon } from './FlagIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_no-sub-pdf/artifacts/8avttwow_logohexoworkssanstitre.png";

const langLabels = {
  en: 'English', fr: 'Français', es: 'Español', de: 'Deutsch',
  it: 'Italiano', pt: 'Português', nl: 'Nederlands', pl: 'Polski',
  tr: 'Türkçe', ru: 'Русский', ja: '日本語', zh: '中文', ar: 'العربية', hi: 'हिन्दी',
};

export const Navbar = () => {
  const { t, lang, switchLang, languages } = useI18n();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { currency, setCurrency, currencies, labels } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isLanding = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (!isLanding) { navigate('/#' + id); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { label: t('nav.features'), action: () => scrollTo('features'), testId: 'nav-features' },
    { label: t('nav.pricing'), to: '/pricing', testId: 'nav-pricing' },
    { label: t('nav.faq'), to: '/faq', testId: 'nav-faq' },
    { label: t('nav.resources') || 'Ressources', to: '/resources', testId: 'nav-resources' },
    { label: 'Blog', to: '/blog', testId: 'nav-blog' },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-background/90 backdrop-blur-xl border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-14 sm:h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <img src={LOGO_ICON} alt="HexoPDF" className="w-7 h-7 sm:w-8 sm:h-8" />
          <span className="font-heading font-bold text-base sm:text-lg text-foreground tracking-tight">
            Hexo<span className="text-blue-500">PDF</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) =>
            link.to ? (
              <Link key={link.testId} to={link.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid={link.testId}>{link.label}</Link>
            ) : (
              <button key={link.testId} onClick={link.action} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid={link.testId}>{link.label}</button>
            )
          )}
        </div>

        {/* Right */}
        <div className="hidden lg:flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-muted-foreground hover:text-foreground px-2" data-testid="theme-toggle">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1 px-2 font-medium text-xs" data-testid="currency-toggle">
                {currency.toUpperCase()}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border min-w-[100px]">
              {currencies.map(c => (
                <DropdownMenuItem key={c} onClick={() => setCurrency(c)}
                  className={`cursor-pointer text-xs ${c === currency ? 'text-blue-500' : 'text-card-foreground'}`}
                  data-testid={`currency-option-${c}`}>
                  {labels[c] || c.toUpperCase()}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1.5 px-2" data-testid="lang-switcher">
                <FlagIcon lang={lang} size={14} />
                {langLabels[lang] || lang.toUpperCase()}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border min-w-[100px]">
              {languages.map(l => (
                <DropdownMenuItem key={l} onClick={() => switchLang(l)}
                  className={`cursor-pointer flex items-center gap-2 ${l === lang ? 'text-blue-500' : 'text-card-foreground'}`} data-testid={`lang-option-${l}`}>
                  <FlagIcon lang={l} size={14} />
                  {langLabels[l] || l.toUpperCase()}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <NotificationBell />
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => navigate('/account')} data-testid="nav-account-btn">
                {t('nav.account')}
              </Button>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={logout} data-testid="nav-logout-btn">
                {t('nav.logout')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => navigate('/login')} data-testid="nav-login-btn">
                {t('nav.login')}
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-4 sm:px-5 text-xs sm:text-sm shadow-[0_0_15px_rgba(59,130,246,0.4)] transition-all"
                onClick={() => navigate('/login', { state: { redirect: '/pricing', message: 'signin_to_buy' } })} data-testid="nav-waitlist-btn">
                {t('nav.signin_buy') || 'Sign in & buy'}
              </Button>
            </>
          )}
        </div>

        {/* Mobile: theme + menu toggle */}
        <div className="flex lg:hidden items-center gap-2">
          <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-muted-foreground px-2">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-t border-border px-4 py-5 space-y-1 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          {navLinks.map((link) =>
            link.to ? (
              <Link key={link.testId} to={link.to} onClick={() => setOpen(false)} className="block py-2.5 text-foreground/80 text-sm">{link.label}</Link>
            ) : (
              <button key={link.testId} onClick={() => { link.action(); setOpen(false); }} className="block w-full text-left py-2.5 text-foreground/80 text-sm">{link.label}</button>
            )
          )}

          <div className="flex items-center gap-3 pt-3 pb-2 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground bg-secondary/50">
                  <FlagIcon lang={lang} size={14} />
                  {langLabels[lang] || lang.toUpperCase()}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-card border-border max-h-[300px] overflow-y-auto">
                {languages.map(l => (
                  <DropdownMenuItem key={l} onClick={() => { switchLang(l); setOpen(false); }}
                    className={`cursor-pointer flex items-center gap-2 text-xs ${l === lang ? 'text-blue-500' : 'text-card-foreground'}`}>
                    <FlagIcon lang={l} size={14} />
                    {langLabels[l] || l.toUpperCase()}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground bg-secondary/50">
                  {currency.toUpperCase()}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-card border-border">
                {currencies.map(c => (
                  <DropdownMenuItem key={c} onClick={() => { setCurrency(c); setOpen(false); }}
                    className={`cursor-pointer text-xs ${c === currency ? 'text-blue-500' : 'text-card-foreground'}`}>
                    {labels[c] || c.toUpperCase()}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex gap-2 pt-2 items-center">
            {user ? (
              <>
                <Button size="sm" variant="ghost" className="text-foreground/80 text-sm" onClick={() => { navigate('/account'); setOpen(false); }}>{t('nav.account')}</Button>
                <Button size="sm" variant="ghost" className="text-muted-foreground text-sm" onClick={() => { logout(); setOpen(false); }}>{t('nav.logout')}</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="ghost" className="text-foreground/80 text-sm" onClick={() => { navigate('/login'); setOpen(false); }}>{t('nav.login')}</Button>
                <Button size="sm" className="bg-blue-600 text-white rounded-full px-4 text-xs" onClick={() => { navigate('/login', { state: { redirect: '/pricing', message: 'signin_to_buy' } }); setOpen(false); }}>{t('nav.signin_buy') || 'Sign in & buy'}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
